/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Render an authorized five-minute private asset grant without durable persistence.
 * @state-owner TanStack Query memory only
 * @allowed-imports same-module API; app environment; shared loading; TanStack Query
 * @forbidden localStorage; indexedDB; provider SDK; durable signed URL state
 * @public-api teacher and student activity content
 * @side-effects short-lived download grant request
 * @tests PrivateAsset.test.tsx
 */

import { useQuery } from '@tanstack/react-query';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { privateAssetGrant } from '../api/private-assets';

export function PrivateAsset({ assetId, alt = '教材附件' }: { assetId: string; alt?: string }) {
  const environment = useWorkspaceEnvironment();
  const grant = useQuery({
    queryKey: ['private-asset-grant', assetId],
    queryFn: () => privateAssetGrant(environment.apiClient, assetId),
    staleTime: 4 * 60 * 1_000,
    gcTime: 0,
    retry: false,
    refetchOnMount: 'always',
  });
  if (grant.isPending) return <VispoLoading label="正在安全載入附件" />;
  if (grant.isError || !grant.data) {
    return (
      <div className="private-asset-error" role="alert">
        <b>附件目前無法開啟</b>
        <span>請確認連線後再試一次。</span>
        <button className="bt sm" type="button" onClick={() => void grant.refetch()}>
          重新載入
        </button>
      </div>
    );
  }
  return grant.data.mimeType.startsWith('image/') ? (
    <img alt={alt} className="private-asset-image" src={grant.data.downloadUrl} />
  ) : (
    <a
      className="private-asset-file"
      href={grant.data.downloadUrl}
      rel="noreferrer"
      target="_blank"
    >
      <b>{grant.data.filename}</b>
      <span>{grant.data.mimeType}</span>
    </a>
  );
}
